// ante-react-kit/src/components/AntQuickPopup/useQuickPopup.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AntCondition from '../../utils/AntCondition';
import resolveEditorUtil from './utils/resolveEditor';
import resolveLookupUtil from './utils/resolveLookup';

/**
 * Layer 1 hook — pure logic, không UI.
 *
 * @param {AntAPI} API
 * @param {Object} options
 * @param {bigint|number} options.id
 * @param {string} options.code
 * @param {boolean} options.recalcable     default true
 * @param {boolean} options.autoReloadOnAction  default true
 * @param {Function} options.onError       (err) => void
 * @param {Object} options.session         session object (truyền vào, không tự lấy)
 * @param {Array}  options.lookupsFE       FE init-lookups registry data
 * @param {Array}  options.editors         FE init-editors registry data
 * @param {Object} options.fieldsCnf       FE config/fields/{Table}.json content
 */
export default function useQuickPopup(API, options = {}) {
    const {
        id, code,
        recalcable = true,
        autoReloadOnAction = true,
        onError,
        session,
        lookupsFE,
        editors,
        fieldsCnf
    } = options;

    const [originalData, setOriginalData] = useState(null);
    const [data, setData] = useState(null);
    const [edit, setEdit] = useState({});
    const [actions, setActions] = useState([]);
    const [infos, setInfos] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [lookupsBE, setLookupsBE] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const dataRef = useRef(null);
    dataRef.current = data;

    const dirtyFieldsRef = useRef(new Set());
    const [dirtyTick, setDirtyTick] = useState(0);

    const dirtyFields = dirtyFieldsRef.current;
    const dirty = dirtyFields.size > 0;

    // ─── Load ──────────────────────────────────
    const reload = useCallback(async () => {
        if (!API || (!id && !code)) return;
        setIsLoading(true);
        setError(null);
        try {
            // postUpdatePrepare resolves with data.data directly (the inner object)
            const res = await API.postUpdatePrepare(id, code, recalcable, undefined, dataRef.current);
            const d = res || {};
            setOriginalData(d);
            setData(d);
            setEdit(d.edit || {});
            setActions(d.actions || []);
            setInfos(d.infos || []);
            setWarnings(d.warnings || []);
            setLookupsBE(d.lookups || {});
            dirtyFieldsRef.current = new Set();
            setDirtyTick((n) => n + 1);
        } catch (err) {
            setError(err);
            if (onError) onError(err);
        } finally {
            setIsLoading(false);
        }
    }, [API, id, code, recalcable, onError]);

    useEffect(() => { reload(); }, [reload]);

    // ─── Mutations ─────────────────────────────
    const setField = useCallback((name, value) => {
        setData((prev) => {
            if (!prev) return prev;
            if (prev[name] === value) return prev;
            return { ...prev, [name]: value };
        });
        dirtyFieldsRef.current.add(name);
        setDirtyTick((n) => n + 1);
    }, []);

    const resetField = useCallback((name) => {
        if (!originalData) return;
        setData((prev) => ({ ...prev, [name]: originalData[name] }));
        dirtyFieldsRef.current.delete(name);
        setDirtyTick((n) => n + 1);
    }, [originalData]);

    const save = useCallback(async () => {
        if (!data || !dirty) return { saved: false };
        const changedFields = {};
        dirtyFieldsRef.current.forEach((k) => { changedFields[k] = data[k]; });
        // postUpdate(obj, newData, opts) — obj is {id, code, checksum}, newData is changed fields
        const where = { id: data.id, code: data.code, checksum: data.checksum };
        try {
            const res = await API.postUpdate(where, changedFields, { getNewPrepare: true }, data);
            // postUpdate resolves with raw BE response {data, error, ...}
            const newData = res && res.data ? res.data : null;
            if (newData) {
                setOriginalData(newData);
                setData(newData);
                if (newData.edit) setEdit(newData.edit);
                if (newData.actions) setActions(newData.actions);
                if (newData.infos) setInfos(newData.infos);
                if (newData.warnings) setWarnings(newData.warnings);
                if (newData.lookups) setLookupsBE(newData.lookups);
            }
            dirtyFieldsRef.current = new Set();
            setDirtyTick((n) => n + 1);
            return { saved: true, data: newData };
        } catch (err) {
            if (onError) onError(err);
            return { saved: false, error: err };
        }
    }, [API, data, dirty, onError]);

    const runAction = useCallback(async (name, params = {}) => {
        if (!data) return { success: false };
        try {
            // postAction(action, code, id, useQuickResponse)
            const res = await API.postAction(name, data.code, data.id, false, data);
            if (autoReloadOnAction && res && res.data) {
                const newData = res.data;
                setOriginalData(newData);
                setData(newData);
                if (newData.edit) setEdit(newData.edit);
                if (newData.actions) setActions(newData.actions);
                if (newData.infos) setInfos(newData.infos);
                if (newData.warnings) setWarnings(newData.warnings);
                if (newData.lookups) setLookupsBE(newData.lookups);
                dirtyFieldsRef.current = new Set();
                setDirtyTick((n) => n + 1);
            }
            return { success: true, data: res };
        } catch (err) {
            if (onError) onError(err);
            return { success: false, error: err };
        }
    }, [API, data, autoReloadOnAction, onError]);

    // ─── Permission/visibility helpers ─────────
    const canEditField = useCallback((name) => {
        if (!edit) return false;
        if (edit.always_editable_fields && edit.always_editable_fields.includes(name)) return true;
        if (edit.isEditable === false) return false;
        if (Array.isArray(edit.editable_fields)) return edit.editable_fields.includes(name);
        return false;
    }, [edit]);

    const canEditChild = useCallback((table) => _checkChild(edit, table, 'canEdit'), [edit]);
    const canAddChild = useCallback((table) => _checkChild(edit, table, 'canAdd'), [edit]);
    const canDeleteChild = useCallback((table) => _checkChild(edit, table, 'canDelete'), [edit]);

    const checkVisible = useCallback((condition) => {
        if (!condition) return true;
        return AntCondition.isValid(data || {}, condition);
    }, [data, dirtyTick]); // eslint-disable-line react-hooks/exhaustive-deps

    const resolveEditor = useCallback((editorName, editorType) => {
        return resolveEditorUtil({ editorName, editorType, data, session, editors });
    }, [data, session, editors, dirtyTick]); // eslint-disable-line react-hooks/exhaustive-deps

    const resolveLookup = useCallback((fieldName) => {
        const fieldCnf = (fieldsCnf || []).find((f) => f.dataField === fieldName);
        return resolveLookupUtil(fieldName, { lookupsBE, lookupsFE, fieldCnf });
    }, [lookupsBE, lookupsFE, fieldsCnf]);

    // ─── Child API factory ─────────────────────
    const childAPI = useCallback((tableName) => {
        // Note: childAPI cần AntAPI riêng cho table con — caller phải truyền.
        // Phase 1 trả về stub; Phase 3 sẽ implement đầy đủ khi build QPChildGrid.
        return {
            list: () => [],
            add: async () => { throw new Error('childAPI: implement in Phase 3'); },
            update: async () => { throw new Error('childAPI: implement in Phase 3'); },
            remove: async () => { throw new Error('childAPI: implement in Phase 3'); }
        };
    }, []);

    return useMemo(() => ({
        data, originalData, edit, actions, infos, warnings,
        session, lookups: lookupsBE, lookupsFE, editors, fieldsCnf,
        isLoading, error, dirty, dirtyFields,
        canEditField, canEditChild, canAddChild, canDeleteChild,
        checkVisible, resolveEditor, resolveLookup,
        setField, resetField, save, runAction, reload, childAPI
    }), [
        data, originalData, edit, actions, infos, warnings,
        session, lookupsBE, lookupsFE, editors, fieldsCnf,
        isLoading, error, dirty, dirtyFields,
        canEditField, canEditChild, canAddChild, canDeleteChild,
        checkVisible, resolveEditor, resolveLookup,
        setField, resetField, save, runAction, reload, childAPI
    ]);
}

function _checkChild(edit, table, key) {
    if (!edit) return false;
    if (edit.withChildren === true) return true;
    if (typeof edit.withChildren === 'object' && !Array.isArray(edit.withChildren)) {
        return !!edit.withChildren[key];
    }
    if (Array.isArray(edit.withChildren)) {
        const found = edit.withChildren.find((w) => w.table === table);
        return found ? !!found[key] : false;
    }
    return false;
}
